import { toast } from 'react-toastify'
import { get } from 'utils/lodash'
import { insertSpaceBeforeCapitalLetter } from 'utils/string'
import { CARD_IMAGES, ACH_IMAGE, PAYMENT_METHODS, ACH_ACCOUNT_TYPE, PAYMENT_METHODS_LABELS } from 'settings/constants/payment'

const { CREDIT_CARD, ACH, ACCOUNT_CREDIT } = PAYMENT_METHODS
const accountCreditLabel = PAYMENT_METHODS_LABELS[ACCOUNT_CREDIT]

export const isValidAccountType = accountType => ACH_ACCOUNT_TYPE.includes(accountType)

export const getPaymentMethodName = (creditCardType, accountType) => {
  if (creditCardType) {
    return CREDIT_CARD
  }

  if (isValidAccountType(accountType)) {
    return ACH
  }

  return ''
}

export const getPaymentMethodImage = type => {
  if (type === ACH) {
    return ACH_IMAGE
  }

  return CARD_IMAGES[type] || CARD_IMAGES.Unknown
}

// Sort invoices by payment order: negative amounts first, then by due date
export const sortInvoicesByPaymentOrder = (invoices, allInvoices) => {
  const invoiceMap = allInvoices.reduce((map, invoice) => {
    map[invoice.invoiceNumber] = invoice;
    return map;
  }, {});

  return invoices.sort((a, b) => {
    const invoiceA = invoiceMap[a.invoiceNumber];
    const invoiceB = invoiceMap[b.invoiceNumber];
    if (!invoiceA || !invoiceB) return 0;

    // Negative amounts first, then by due date
    const amountA = invoiceA.usedPartialAmount || invoiceA.invoiceAmount || 0;
    const amountB = invoiceB.usedPartialAmount || invoiceB.invoiceAmount || 0;
    const priorityA = amountA < 0 ? 0 : 1;
    const priorityB = amountB < 0 ? 0 : 1;

    return priorityA !== priorityB ? priorityA - priorityB : new Date(invoiceA.dueDate) - new Date(invoiceB.dueDate);
  });
}

// All values in Cents
export const handleInvoiceSelectDeselect = (
  allInvoices,
  initialCreditBalanceCents,
  addedAmount,
  newlySelectedInvoiceNumbers = [],
  sortedUnpaidInvoices = [],
  showToastMessages = true
) => {
  let selectedInvoices = []

  if (!Array.isArray(allInvoices)) {
    return { isChanged: false, selectedInvoices }
  }

  let creditBalanceClone = initialCreditBalanceCents || 0
  let addedAmountClone = addedAmount || 0

  // Sort invoices by payment order when we have sorted unpaid invoices context
  const invoicesToProcess =
    sortedUnpaidInvoices.length > 0
      ? sortInvoicesByPaymentOrder(allInvoices, sortedUnpaidInvoices)
      : allInvoices

  let isPartial = false
  const originalInvoiceCount = allInvoices.length

  // Process invoices in payment precedence order
  invoicesToProcess.forEach((invoice, index) => {
    const { invoiceAmount, invoiceNumber } = invoice
    const totalAddedAmount = addedAmountClone + creditBalanceClone

    // Only one partial invoice allowed
    // Ignore for $0
    if (invoiceAmount && (totalAddedAmount <= 0 || isPartial)) {
      return
    }

    const usedAmount = addedAmountClone >= invoiceAmount ? invoiceAmount : addedAmountClone
    const leftAmount = addedAmountClone >= invoiceAmount ? 0 : invoiceAmount - addedAmountClone

    // Use account credit for the pending amount
    let usedCreditBalance = 0
    if (leftAmount) {
      usedCreditBalance = creditBalanceClone >= leftAmount ? leftAmount : creditBalanceClone
    }

    // Check for partial case
    isPartial = invoiceAmount > totalAddedAmount
    const usedPartialAmount = isPartial ? totalAddedAmount : 0

    // Reduce amount for next invoices
    addedAmountClone -= usedAmount
    creditBalanceClone -= usedCreditBalance

    // Only set extraBalance for the last invoice and only if there's actually leftover payment
    const isLastInvoice = index === invoicesToProcess.length - 1
    const extraBalance = isLastInvoice && addedAmountClone > 0 ? addedAmountClone : 0

    selectedInvoices = [
      ...selectedInvoices,
      {
        isLocked: invoice.isLocked,
        invoiceNumber,
        invoiceAmount,
        usedAmount,
        usedCreditBalance,
        // Can be extra balance which will be added to the account credit
        extraBalance,
        isPartial,
        usedPartialAmount,
      },
    ]
  })

  // Show toast if invoices were filtered out due to insufficient payment
  if (showToastMessages && selectedInvoices.length < originalInvoiceCount) {
    const processedInvoiceNumbers = selectedInvoices.map(inv => inv.invoiceNumber)
    const removedInvoices = allInvoices.filter(invoice => !processedInvoiceNumbers.includes(invoice.invoiceNumber))
    const newlySelectedWasRemoved = newlySelectedInvoiceNumbers.length > 0 && 
      removedInvoices.some(invoice => newlySelectedInvoiceNumbers.includes(invoice.invoiceNumber))

    // Determine scenario based on newlySelectedInvoiceNumbers
    const isSelectAllScenario = newlySelectedInvoiceNumbers.length > 1
    const isSingleInvoiceSelection = newlySelectedInvoiceNumbers.length === 1
    const noInvoicesSelected = selectedInvoices.length === 0

    if (isSelectAllScenario) {
      // Special message for Select All scenarios (multiple invoices being selected)
      const removedCount = removedInvoices.length
      const removedText = removedCount === 1 ? 'invoice could not be selected' : 'invoices could not be selected'
      toast.info(`${removedCount} ${removedText} due to insufficient funds.`, {
        autoClose: 4000,
      })
    } else if (isSingleInvoiceSelection && newlySelectedWasRemoved && noInvoicesSelected) {
      // The single invoice the user tried to select was removed and no invoices are selected
      const removedCount = removedInvoices.length
      const removedText = removedCount === 1 ? 'invoice could not be selected' : 'invoices could not be selected'
      toast.info(`${removedCount} ${removedText} due to insufficient funds.`, {
        autoClose: 4000,
      })
    } else if (isSingleInvoiceSelection && newlySelectedWasRemoved) {
      // The single invoice the user just tried to select was removed (but other invoices are selected)
      const removedCount = removedInvoices.length
      const removedText = removedCount === 1 ? 'invoice could not be selected' : 'invoices could not be selected'
      toast.info(`${removedCount} ${removedText} because earlier invoices consume the full payment amount.`, {
        autoClose: 4000,
      })
    } else if (isSingleInvoiceSelection && !newlySelectedWasRemoved) {
      // Other previously selected invoices were removed when user selected a single invoice
      const removedCount = removedInvoices.length
      const removedText = removedCount === 1 ? 'invoice was' : 'invoices were'
      toast.info(`${removedCount} ${removedText} auto-unselected because earlier invoices consume the full payment amount.`, {
        autoClose: 4000,
      })
    }
  }

  return { isChanged: true, selectedInvoices }
}

export const isAmountAvailable = (totalUnpaid, initialAvailableCredit, newAmount) => {
  if (totalUnpaid > initialAvailableCredit + (newAmount ? parseFloat(newAmount) : 0)) {
    toast.info('Available credit is less than the invoice selected')
    return false
  }

  return true
}

// remove invoice amount
export const removeInvoiceAmountFromPayload = payload => ({
  ...payload,
  requestedPayments: payload.requestedPayments.map(payment => ({
    ...payment,
    invoicesToPay: payment.invoicesToPay.map(invoice => invoice.invoiceNumber),
  })),
})

const getSingleSummaryRow = (refId, accountId, customerName, invoiceNumber, checkNum, paymentMethod, amount, totalInvoices) => ({
  refId,
  accountId,
  customerName,
  invoiceNumber,
  checkNum,
  paymentMethod,
  amount,
  totalInvoices,
})

export const getSummaryTableData = requestedPayments => {
  let summaryRows = []
  let total = 0
  requestedPayments.forEach(account => {
    const { refId, accountId, customerName, invoicesToPay } = account
    const checkNum = get(account, 'checkNum', '')
    const paymentMethod = get(account, 'paymentMethod', '')

    const { amountCents } = account

    if (invoicesToPay.length === 0) {
      // if no invoice and amount
      summaryRows = [
        ...summaryRows,
        getSingleSummaryRow(refId, accountId, customerName, accountCreditLabel, checkNum, paymentMethod, amountCents, 0),
      ]
      total += amountCents
    }

    invoicesToPay.forEach((invoice, index) => {
      const { invoiceNumber, usedAmount, usedCreditBalance, extraBalance } = invoice

      if (usedAmount) {
        summaryRows = [
          ...summaryRows,
          getSingleSummaryRow(refId, accountId, customerName, invoiceNumber, checkNum, paymentMethod, usedAmount, invoicesToPay.length),
        ]
        total += usedAmount
      }

      // Use account credit
      if (usedCreditBalance || !usedAmount) {
        summaryRows = [
          ...summaryRows,
          getSingleSummaryRow(
            refId,
            accountId,
            customerName,
            invoiceNumber,
            accountCreditLabel,
            ACCOUNT_CREDIT,
            usedCreditBalance,
            invoicesToPay.length
          ),
        ]
        total += usedCreditBalance
      }

      if (invoicesToPay.length - 1 === index && extraBalance) {
        // add left over balance
        summaryRows = [
          ...summaryRows,
          getSingleSummaryRow(refId, accountId, customerName, accountCreditLabel, checkNum, paymentMethod, extraBalance, 0),
        ]
        total += extraBalance
      }
    })
  })

  return { summaryRows, total }
}

export const getPaymentMethod = paymentMethod =>
  insertSpaceBeforeCapitalLetter(Array.isArray(paymentMethod) ? paymentMethod.join(',') : paymentMethod)

export const getRevertPaymentConfirmationMsg = (hasLinkedAccountCredit = false, invoices = [], hasPartialInvoice = false) => {
  let message = ''

  if (invoices.length) {
    const joinInvoices = invoices.join(', ')
    const isSingleInvoice = invoices.length === 1
    if (hasPartialInvoice) {
      return `Reverting this payment will affect invoice${isSingleInvoice ? '' : 's'} ${joinInvoices}.`
    }

    message = `Reverting this payment will change invoice${isSingleInvoice ? '' : 's'} ${joinInvoices} to Unpaid.`

    if (hasLinkedAccountCredit) {
      message += ` Any excess payment that went to account credit will also be reverted.`
    }

    return message
  }

  return 'Please move the payment to account credit on all affected invoices before proceeding with the revert process. The system does not have information about which specific invoice was paid using the account credit that you would like to have the payment pulled out from.'
}
